import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { PaginatorControlComponent } from '@app/shared/components/paginator-control/paginator-control.component';
import { Pagination } from '@app/shared/model';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { BadgeModule } from 'primeng/badge';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { RadioButton } from 'primeng/radiobutton';
import { Menu } from 'primeng/menu';

// export interface TableColumn {
//   field?: string;
//   header: string;
//   styleClass?: string;
//   tooltip?: string;
//   type?: 'text' | 'tag' | 'badge' | 'radio';
//   severityField?: string;
//   formatter?: (value: any, rowData: any) => any;
//   valueGetter?: (rowData: any) => any;
//   tagSeverityFunction?: (
//     rowData: any
//   ) =>
//     | 'success'
//     | 'info'
//     | 'warn'
//     | 'danger'
//     | 'secondary'
//     | 'contrast'
//     | 'primary';
//   badgeSeverityFunction?: (
//     rowData: any
//   ) => 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast';
// }
export interface TableColumn {
  field?: string;
  header: string;
  styleClass?: string;
  tooltip?: string;
  type?: 'text' | 'tag' | 'badge' | 'radio' | 'date'; // ⬅️ adiciona 'date'
  severityField?: string;
  formatter?: (value: any, rowData: any) => any;
  valueGetter?: (rowData: any) => any;
  tagSeverityFunction?: (
    rowData: any
  ) =>
    | 'success'
    | 'info'
    | 'warn'
    | 'danger'
    | 'secondary'
    | 'contrast'
    | 'primary';
  badgeSeverityFunction?: (
    rowData: any
  ) => 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast';
  /** ⬇️ formato do date pipe (ex.: 'dd/MM/yyyy', 'dd/MM/yyyy HH:mm') */
  dateFormat?: string;
}

@Component({
  selector: 'app-table',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    PaginatorControlComponent,
    ButtonModule,
    BadgeModule,
    TagModule,
    MenuModule,
    TooltipModule,
    ReactiveFormsModule,
    RadioButton,
    Menu,
  ],
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit, OnChanges {
  @Input() pagination: Pagination<any> = new Pagination<any>();
  @Input() columns: TableColumn[] = [];
  @Input() actions: ((rowData: any) => MenuItem[]) | MenuItem[] = [];
  @Input() selectedRadioValue: any;
  @Input() alwaysShowPaginator: boolean = true;
  openedMenu: Menu | null = null;
  @Output() pageChange = new EventEmitter<any>();
  @Output() actionSelected = new EventEmitter<{ event: any; rowData: any }>();
  @Output() radioSelectionChange = new EventEmitter<any>();
  @Output() rowClick = new EventEmitter<any>();

  form: FormGroup = new FormGroup({});

  ngOnInit() {
    this.updateMenuActions();
    this.initializeForm();
  }

  ngOnChanges() {
    this.updateMenuActions();
    this.initializeForm();
  }

  onPageChange(event: any) {
    this.pageChange.emit(event);
  }

  onActionSelected(event: any, rowData: any) {
    this.actionSelected.emit({ event, rowData });
  }

  initializeForm() {
    if (!this.form.contains('selectedRadio')) {
      this.form.addControl(
        'selectedRadio',
        new FormControl(this.selectedRadioValue)
      );
    }
    this.form.valueChanges.subscribe((value) => {});
  }

  getRadioFormControl(): FormControl {
    return this.form.get('selectedRadio') as FormControl;
  }

  onRadioChange(rowData: any) {
    this.radioSelectionChange.emit(rowData);
  }

  updateMenuActions() {
    if (this.pagination.data) {
      this.pagination.data = this.pagination.data.map((rowData: any) => ({
        ...rowData,
        menuActions: this.getMenuActions(rowData),
      }));
    }
  }

  getMenuActions(rowData: any): MenuItem[] {
    if (typeof this.actions === 'function') {
      return this.actions(rowData);
    }

    return this.actions.map((action) => ({
      ...action,
      data: rowData,
    }));
  }

  getTagSeverity(
    col: TableColumn,
    rowData: any
  ):
    | 'success'
    | 'info'
    | 'warn'
    | 'danger'
    | 'secondary'
    | 'contrast'
    | undefined {
    if (col.tagSeverityFunction) {
      return col.tagSeverityFunction(rowData) as any;
    }
    return col.severityField ? rowData[col.severityField] : undefined;
  }

  getBadgeSeverity(
    col: TableColumn,
    rowData: any
  ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    if (col.badgeSeverityFunction) {
      return col.badgeSeverityFunction(rowData);
    }
    return col.severityField ? rowData[col.severityField] : 'info';
  }

  getFieldValue(rowData: any, col: TableColumn): any {
    // 1) valueGetter tem prioridade
    if (col.valueGetter) {
      const v = col.valueGetter(rowData);
      return col.formatter ? col.formatter(v, rowData) : v;
    }

    // 2) fallback para field (opcional)
    const field = col.field ?? '';
    let value: any;

    if (field.includes('.')) {
      value = field
        .split('.')
        .reduce((acc, part) => (acc ? acc[part] : undefined), rowData);
    } else if (field) {
      value = rowData[field];
    } else {
      value = undefined;
    }

    return col.formatter ? col.formatter(value, rowData) : value;
  }

  onRowClick(rowData: any) {
    this.rowClick.emit(rowData);
  }

  toggleMenu(event: any, menu: Menu) {
    if (this.openedMenu && this.openedMenu !== menu) {
      this.openedMenu.hide();
    }
    this.openedMenu = menu;
    menu.toggle(event);
  }
}
